import { useMutation } from "@tanstack/vue-query";
import type { Ref } from "vue";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { ApiError } from "@/helpers/api-error";
import { useSessionStore } from "@/stores/session-store";

const AUTH_BASE_URL = `${import.meta.env.VITE_API_URL}/auth`;

function toBrowserUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

interface LoginPayload {
  email: string;
  password: string;
  orgHint?: string;
}

export function useLoginPassword() {
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, LoginPayload>({
    mutationFn: (payload) =>
      fetchWrapper.post(`${AUTH_BASE_URL}/login/password`, payload, { skipAuthRedirect: true }),
    onSuccess: () => useSessionStore().syncSession(),
  });

  return {
    loginPassword: mutate,
    loginPasswordAsync: mutateAsync,
    loginPasswordPending: isPending,
    loginPasswordError: error as Ref<ApiError | null>,
    loginPasswordReset: reset,
  };
}

interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export function useChangePassword() {
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<void, ApiError, ChangePasswordPayload>({
    // skipAuthRedirect: surface any error in the modal rather than bouncing the user to /login
    // mid-form. (The backend returns 400 — not 401 — for an incorrect current password.)
    mutationFn: (payload) => fetchWrapper.post(`${AUTH_BASE_URL}/change-password`, payload, { skipAuthRedirect: true }),
  });

  return {
    changePassword: mutate,
    changePasswordAsync: mutateAsync,
    changePasswordPending: isPending,
    changePasswordError: error as Ref<ApiError | null>,
    changePasswordReset: reset,
  };
}

export function startSsoLogin(email?: string, orgHint?: string, returnUrl = window.location.href): void {
  const store = useSessionStore();
  store.clearSession();
  let url = `${toBrowserUrl(`${AUTH_BASE_URL}/login`)}?returnUrl=${encodeURIComponent(returnUrl)}`;
  if (email) {
    url += `&email=${encodeURIComponent(email)}`;
  }
  if (orgHint) {
    url += `&orgHint=${encodeURIComponent(orgHint)}`;
  }
  window.location.href = url;
}

export function logout(): void {
  const store = useSessionStore();
  store.clearSession();
  const returnUrl = `${window.location.origin}/login`;
  window.location.href = `${toBrowserUrl(`${AUTH_BASE_URL}/logout`)}?returnUrl=${encodeURIComponent(returnUrl)}`;
}
